/* eslint-disable @typescript-eslint/ban-ts-comment */
import AutoSizer from "react-virtualized-auto-sizer";
import { themeColors, themeBorderColors } from "@common/constants";
import React, { FC } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  TooltipProps,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";
import { FILLS_BY_IMPORTANCE_GROUP } from "@modules/financialplan/constants";
import { currencyFormatter, last2DigitYearFormatter } from "@common/utils";
import XAxisTick from "@common/components/XAxisTick";
import YAxisTick from "@common/components/YAxisTick";
import ChartTooltip from "@common/components/ChartTooltip";
import {
  useFinancialGoalsValues,
  useExpenseTimelineValues,
} from "@modules/financialplan/hooks";
import { ScenarioID } from "@modules/mgp-sso/types";
import LoadingIndicator from "@common/components/LoadingIndicator";
import { Typography } from "@earned/core";

export interface GoalTimelineChart {
  year: number;
  goals: Forme.Goal[];
}

interface GoalTimelineProps {
  scenario: ScenarioID;
}

const GoalTimeline: FC<GoalTimelineProps> = ({ scenario }) => {
  const { goalsByImportanceGroup, isLoadingFinancialGoals } =
    useFinancialGoalsValues();
  const { expenseTimeline } = useExpenseTimelineValues(scenario);

  // Load in background the other scenario.
  useExpenseTimelineValues(
    scenario === ScenarioID.Recommended
      ? ScenarioID.Current
      : ScenarioID.Recommended
  );

  return (
    <>
      <LoadingIndicator isVisible={isLoadingFinancialGoals} />
      {!isLoadingFinancialGoals && (
        <AutoSizer>
          {({ width }: { width: number }) => (
            <BarChart
              width={width}
              height={318}
              data={expenseTimeline}
              className="top-5"
              margin={{ left: -22, bottom: 66, top: 28 }}
            >
              <CartesianGrid
                strokeDasharray="0"
                vertical={false}
                stroke={themeBorderColors.primary}
              />
              <XAxis
                tickLine={false}
                padding={{ left: 30 }}
                dataKey="year"
                axisLine={{ stroke: themeBorderColors.primary }}
                tickFormatter={last2DigitYearFormatter}
                tick={XAxisTick}
              />
              <YAxis
                axisLine={false}
                tick={YAxisTick}
                tickLine={false}
                tickFormatter={(value) =>
                  `${currencyFormatter(value, 0, true)}`
                }
              />
              <Tooltip
                cursor={{ fill: themeColors.gray[50] }}
                content={(props: TooltipProps<ValueType, NameType>) => {
                  return (
                    <ChartTooltip title={props.label}>
                      {props?.payload
                        ?.slice(0)
                        ?.reverse()
                        ?.filter((expense) => (Number(expense?.value) ?? 0) > 0)
                        ?.map((expense) => (
                          <div
                            key={expense.name}
                            className="mb-2 flex h-4 flex-row items-center justify-between space-x-6 last:mb-0"
                          >
                            <div className="flex flex-row items-center space-x-1">
                              <div
                                className="mr-2 h-3 w-3 rounded-full"
                                style={{ background: expense.color }}
                              />

                              <Typography variant="body" color="primary">
                                {expense.name}
                              </Typography>
                            </div>
                            <Typography variant="body" color="secondary">
                              {currencyFormatter(expense.value as number, 0)}
                            </Typography>
                          </div>
                        ))}
                    </ChartTooltip>
                  );
                }}
              />
              {(["Need", "Want", "Wish"] as Forme.ImportanceGroup[]).map(
                (importanceGroup) =>
                  goalsByImportanceGroup[importanceGroup]?.map(
                    (goal: Forme.FinancialGoal, index: number) => (
                      <Bar
                        key={goal.name}
                        dataKey={goal.name}
                        maxBarSize={11}
                        stackId="year"
                        fill={
                          FILLS_BY_IMPORTANCE_GROUP[
                            importanceGroup as Forme.ImportanceGroup
                          ][index]
                        }
                      />
                    )
                  )
              )}
            </BarChart>
          )}
        </AutoSizer>
      )}
    </>
  );
};

export default GoalTimeline;
